<script setup>
import { computed, onMounted, ref } from 'vue'

const props = defineProps({
	zIndex: {
		type: String,
		default: 'z-50',
	},
	wrapperClass: {
		type: String,
		default: '',
	},
	disableSlotTransition: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits({
	'overlay-click': () => true,
})

const overlayClick = (event) => {
  emit('overlay-click', event)
}

// Take the focus on displaying this modal.  Partial implementation of
// https://uxdesign.cc/how-to-trap-focus-inside-modal-to-make-it-ada-compliant-6a50f9a70700
const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

const rootElement = ref(null)

onMounted(() => {
  if (rootElement.value) {
    const firstElement = rootElement.value.querySelectorAll(focusableElements)[0]
    firstElement?.focus()
  }
})
</script>

<template>
  <div
    class="flex items-center flex-col justify-center overflow-hidden fixed inset-0 modal-overflow"
		:class="[props.zIndex, props.wrapperClass]"
    ref="rootElement"
  >
    <transition
      enter-active-class="transition duration-0 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-0 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        class="absolute inset-0 overlay-backdrop"
        @click.stop.prevent="overlayClick"
      />
    </transition>
    <component :is="props.disableSlotTransition ? 'div' : 'Transition'"
      v-bind="props.disableSlotTransition ? {} : {
        enterActiveClass: 'transition duration-300 ease-in',
        enterFromClass: 'transform scale-50 opacity-0',
        enterToClass: 'transform scale-100 opacity-100',
        leaveActiveClass: 'animate-fadeOut',
      }"
    >
      <slot />
    </component>
  </div>
</template>

<style scoped>
/* The blur is animated via backdrop-filter (not opacity) because
   backdrop-filter ignores ancestor opacity and would pop in/out.
   The enter/leave ramp lives in ModalBox as keyframe animations. */
.overlay-backdrop {
	background-color: rgb(0 0 0 / 0.45);
	-webkit-backdrop-filter: blur(4px);
	backdrop-filter: blur(4px);
}
</style>
